import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import SEOHead from '../components/SEOHead';
import JsonLd from '../components/JsonLd';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const Home = () => {
  const { user } = useAuth();
  const [businessHours, setBusinessHours] = useState('');
  const [services, setServices] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [sizingEnabled, setSizingEnabled] = useState(false);
  const [sizeCategories, setSizeCategories] = useState([]);
  const [bookableServices, setBookableServices] = useState([]);
  const [hero, setHero] = useState({ 
    title: '', 
    subtitle: '',
    titleColor: '#1f2937',
    titleFontSize: '48',
    titleFontFamily: 'system-ui',
    subtitleColor: '#4b5563',
    subtitleFontSize: '20',
    subtitleFontFamily: 'system-ui'
  });
  const [features, setFeatures] = useState([]);
  const [featuresSection, setFeaturesSection] = useState({
    title: 'Why Choose Us?',
    titleColor: '#1f2937',
    titleFontSize: '36',
    titleFontFamily: 'system-ui'
  });
  const [servicesSection, setServicesSection] = useState({
    title: 'Our Services',
    titleColor: '#1f2937',
    titleFontSize: '36',
    titleFontFamily: 'system-ui'
  });
  const [cta, setCta] = useState({
    subtitle: 'Join hundreds of happy customers who trust us with their needs',
    button2Label: 'My Profile',
  });
  const [images, setImages] = useState({
    heroBackground: '',
    heroOverlay: 'true',
    heroOpacity: '0.5',
    heroBackgroundPosition: 'center',
    aboutImageLeft: '',
    aboutImageRight: '',
    ctaImageLeft: '',
    ctaImageRight: ''
  });

  useEffect(() => {
    loadData();
    loadImages();
  }, []);

  const loadData = async () => {
    try {
      const [hoursRes, servicesRes, pricingRes, heroRes, featuresRes, featuresSectionRes, servicesSectionRes, sizingSettingsRes, ctaRes] = await Promise.all([
        axios.get(`${API_URL}/business-info/business_hours`).catch(() => ({ data: { info_value: '' } })),
        axios.get(`${API_URL}/business-info/services_info`).catch(() => ({ data: { info_value: '[]' } })),
        axios.get(`${API_URL}/business-info/pricing_info`).catch(() => ({ data: { info_value: '{}' } })),
        axios.get(`${API_URL}/business-info/hero_section`).catch(() => ({ data: { info_value: '{}' } })),
        axios.get(`${API_URL}/business-info/features_info`).catch(() => ({ data: { info_value: '[]' } })),
        axios.get(`${API_URL}/business-info/features_section`).catch(() => ({ data: { info_value: '{}' } })),
        axios.get(`${API_URL}/business-info/services_section`).catch(() => ({ data: { info_value: '{}' } })),
        axios.get(`${API_URL}/size-categories/settings`).catch(() => ({ data: { sizing_enabled: 'false' } })),
        axios.get(`${API_URL}/business-info/cta_section`).catch(() => ({ data: { info_value: '{}' } })),
      ]);

      setBusinessHours(hoursRes.data.info_value || 'Monday – Friday: 9:00 AM – 5:00 PM\nSaturday: 10:00 AM – 3:00 PM\nSunday: Closed');
      setServices(JSON.parse(servicesRes.data.info_value || '[]'));
      setPricing(JSON.parse(pricingRes.data.info_value || '{}'));
      setSizingEnabled(sizingSettingsRes.data.sizing_enabled === 'true');

      const loadedCta = JSON.parse(ctaRes.data.info_value || '{}');
      setCta({
        subtitle: loadedCta.subtitle || 'Join hundreds of happy customers who trust us with their needs',
        button2Label: loadedCta.button2Label || 'My Profile',
      });
      
      // Get size categories from the API
      const sizeCatsData = await axios.get(`${API_URL}/size-categories/active`).catch(() => ({ data: [] }));
      setSizeCategories(sizeCatsData.data || []);
      
      // Get bookable services with pricing (only active ones)
      const servicesData = await axios.get(`${API_URL}/services?active=true`).catch(() => ({ data: [] }));
      const bookable = servicesData.data || [];
      setBookableServices(bookable);

      // If services_info is not configured, derive display cards from actual services table
      const parsedServicesInfo = JSON.parse(servicesRes.data.info_value || '[]');
      if (parsedServicesInfo.length === 0 && bookable.length > 0) {
        setServices(bookable.map(s => ({
          title: s.name,
          description: s.description || '',
          price: s.base_price ? `$${parseFloat(s.base_price).toFixed(0)}` : '',
        })));
      }
      
      // Merge hero data with defaults to ensure all properties exist
      const loadedHero = JSON.parse(heroRes.data.info_value || '{}');
      console.log('[Home] Loaded hero data:', loadedHero);
      setHero({
        title: loadedHero.title || 'Professional Dog Grooming Services',
        subtitle: loadedHero.subtitle || 'Book your appointment online and give your furry friend the care they deserve',
        titleColor: loadedHero.titleColor || '#1f2937',
        titleFontSize: loadedHero.titleFontSize || '48',
        titleFontFamily: loadedHero.titleFontFamily || 'system-ui',
        subtitleColor: loadedHero.subtitleColor || '#4b5563',
        subtitleFontSize: loadedHero.subtitleFontSize || '20',
        subtitleFontFamily: loadedHero.subtitleFontFamily || 'system-ui',
        bookNowLabel: loadedHero.bookNowLabel || 'Book Now',
        bookNowBgColor: loadedHero.bookNowBgColor || '#ffffff',
        bookNowTextColor: loadedHero.bookNowTextColor || '#2563eb',
        bookNowFontSize: loadedHero.bookNowFontSize || '18',
        myApptsLabel: loadedHero.myApptsLabel || 'My Appointments',
        myApptsBgColor: loadedHero.myApptsBgColor || '#ffffff',
        myApptsTextColor: loadedHero.myApptsTextColor || '#2563eb',
        myApptsFontSize: loadedHero.myApptsFontSize || '18',
        myProfileLabel: loadedHero.myProfileLabel || 'My Profile',
        myProfileBgColor: loadedHero.myProfileBgColor || '#ffffff',
        myProfileTextColor: loadedHero.myProfileTextColor || '#2563eb',
        myProfileFontSize: loadedHero.myProfileFontSize || '18',
      });
      
      // Merge features with defaults
      const loadedFeatures = JSON.parse(featuresRes.data.info_value || '[]');
      if (loadedFeatures.length > 0) {
        setFeatures(loadedFeatures);
      } else {
        setFeatures([
          { title: 'Easy Online Booking', description: 'Schedule appointments 24/7 with our user-friendly booking system. Choose your preferred groomer and time slot.', iconType: 'emoji', iconValue: '📅' },
          { title: 'Expert Care', description: 'Our experienced groomers treat every pet with love and care, ensuring a stress-free grooming experience.', iconType: 'emoji', iconValue: '❤️' },
          { title: 'Flexible Scheduling', description: 'Real-time availability updates and automatic reminders ensure you never miss an appointment.', iconType: 'emoji', iconValue: '🕐' },
        ]);
      }
      
      // Merge featuresSection with defaults
      const loadedFeaturesSection = JSON.parse(featuresSectionRes.data.info_value || '{}');
      console.log('[Home] Loaded features section:', loadedFeaturesSection);
      setFeaturesSection({
        title: loadedFeaturesSection.title || 'Why Choose Us?',
        titleColor: loadedFeaturesSection.titleColor || '#1f2937',
        titleFontSize: loadedFeaturesSection.titleFontSize || '36',
        titleFontFamily: loadedFeaturesSection.titleFontFamily || 'system-ui'
      });
      
      // Merge servicesSection with defaults
      const loadedServicesSection = JSON.parse(servicesSectionRes.data.info_value || '{}');
      console.log('[Home] Loaded services section:', loadedServicesSection);
      setServicesSection({
        title: loadedServicesSection.title || 'Our Services',
        titleColor: loadedServicesSection.titleColor || '#1f2937',
        titleFontSize: loadedServicesSection.titleFontSize || '36',
        titleFontFamily: loadedServicesSection.titleFontFamily || 'system-ui'
      });
    } catch (error) {
      console.error('Load data error:', error);
      // Use defaults if not found
      setBusinessHours('Monday - Friday: 9:00 AM - 5:00 PM\nSaturday: 10:00 AM - 3:00 PM\nSunday: Closed');
      setServices([
        { title: 'Full Groom', description: 'Complete grooming service', price: 'Starting at $72' },
        { title: 'Bath & Tidy', description: 'Bath and basic grooming', price: 'Starting at $72' },
        { title: 'Specialty Cuts', description: 'Breed-specific cuts', price: 'Starting at $72' },
      ]);
      setPricing({
        small: { price: '$72', weight: 'Under 20 lbs' },
        medium: { price: '$89', weight: '20-50 lbs' },
        large: { price: '$125', weight: '50-90 lbs' },
        giant: { price: '$140', weight: '90+ lbs' },
      });
      setHero({
        title: 'Professional Dog Grooming Services',
        subtitle: 'Book your appointment online and give your furry friend the care they deserve',
        titleColor: '#1f2937',
        titleFontSize: '48',
        titleFontFamily: 'system-ui',
        subtitleColor: '#4b5563',
        subtitleFontSize: '20',
        subtitleFontFamily: 'system-ui'
      });
      setFeatures([
        { 
          title: 'Easy Online Booking', 
          description: 'Schedule appointments 24/7 with our user-friendly booking system. Choose your preferred groomer and time slot.',
          iconType: 'emoji',
          iconValue: '📅'
        },
        { 
          title: 'Expert Care', 
          description: 'Our experienced groomers treat every pet with love and care, ensuring a stress-free grooming experience.',
          iconType: 'emoji',
          iconValue: '❤️'
        },
        { 
          title: 'Flexible Scheduling', 
          description: 'Real-time availability updates and automatic reminders ensure you never miss an appointment.',
          iconType: 'emoji',
          iconValue: '🕐'
        }
      ]);
    }
  };

  const loadImages = async () => {
    try {
      const [heroBackgroundRes, heroOverlayRes, heroOpacityRes, heroPositionRes, aboutLeftRes, aboutRightRes, ctaLeftRes, ctaRightRes] = await Promise.all([
        axios.get(`${API_URL}/business-info/hero_background_image`).catch(() => ({ data: { info_value: '' } })),
        axios.get(`${API_URL}/business-info/hero_background_overlay`).catch(() => ({ data: { info_value: 'true' } })),
        axios.get(`${API_URL}/business-info/hero_background_opacity`).catch(() => ({ data: { info_value: '0.5' } })),
        axios.get(`${API_URL}/business-info/hero_background_position`).catch(() => ({ data: { info_value: 'center' } })),
        axios.get(`${API_URL}/business-info/about_section_image_left`).catch(() => ({ data: { info_value: '' } })),
        axios.get(`${API_URL}/business-info/about_section_image_right`).catch(() => ({ data: { info_value: '' } })),
        axios.get(`${API_URL}/business-info/cta_section_image_left`).catch(() => ({ data: { info_value: '' } })),
        axios.get(`${API_URL}/business-info/cta_section_image_right`).catch(() => ({ data: { info_value: '' } }))
      ]);

      setImages({
        heroBackground: heroBackgroundRes.data.info_value ?? '',
        heroOverlay: heroOverlayRes.data.info_value ?? 'true',
        heroOpacity: heroOpacityRes.data.info_value ?? '0.5',
        heroBackgroundPosition: heroPositionRes.data.info_value ?? 'center',
        aboutImageLeft: aboutLeftRes.data.info_value ?? '',
        aboutImageRight: aboutRightRes.data.info_value ?? '',
        ctaImageLeft: ctaLeftRes.data.info_value ?? '',
        ctaImageRight: ctaRightRes.data.info_value ?? ''
      });
    } catch (error) {
      console.error('Load images error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* SEO: home is the canonical page for the LocalBusiness schema —
          it carries the site-wide title and the structured-data block that
          tells Google what kind of business this is, where, and how to
          contact you. The titleOverride flag uses the configured site
          title verbatim instead of "<page> | <site>". */}
      <SEOHead titleOverride />
      <JsonLd type="LocalBusiness" />

      {/* Hero Section */}
      <div 
        className="relative text-white py-20"
        style={{
          backgroundImage: images.heroBackground 
            ? `url(${images.heroBackground})` 
            : 'linear-gradient(to right, var(--color-primary-600), var(--color-primary-800))',
          backgroundSize: 'cover',
          backgroundPosition: images.heroBackgroundPosition || 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Overlay */}
        {images.heroBackground && images.heroOverlay === 'true' && (
          <div 
            className="absolute inset-0 bg-black"
            style={{ opacity: images.heroOpacity }}
          />
        )}
        
        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <h1 
            className="font-bold mb-6"
            style={{
              fontSize: `${hero.titleFontSize || 48}px`,
              fontFamily: hero.titleFontFamily || 'system-ui',
              color: hero.titleColor || '#ffffff'
            }}
          >
            {hero.title || 'Professional Dog Grooming Services'}
          </h1>
          <p 
            className="mb-8"
            style={{
              fontSize: `${hero.subtitleFontSize || 20}px`,
              fontFamily: hero.subtitleFontFamily || 'system-ui',
              color: hero.subtitleColor || '#f3f4f6'
            }}
          >
            {hero.subtitle || 'Book your appointment online and give your furry friend the care they deserve'}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {user && (
              <Link
                to="/appointments"
                className="btn px-8 py-3"
                style={{
                  backgroundColor: hero.myApptsBgColor || '#2563eb',
                  color: hero.myApptsTextColor || '#ffffff',
                  fontSize: `${hero.myApptsFontSize || 18}px`,
                }}
              >
                {hero.myApptsLabel || 'My Appointments'}
              </Link>
            )}
            <Link
              to="/book"
              className="btn px-8 py-3"
              style={{
                backgroundColor: hero.bookNowBgColor || '#ffffff',
                color: hero.bookNowTextColor || '#2563eb',
                fontSize: `${hero.bookNowFontSize || 18}px`,
              }}
            >
              {hero.bookNowLabel || 'Book Now'}
            </Link>
            {user && (
              <Link
                to="/profile"
                className="btn px-8 py-3"
                style={{
                  backgroundColor: hero.myProfileBgColor || '#6b7280',
                  color: hero.myProfileTextColor || '#ffffff',
                  fontSize: `${hero.myProfileFontSize || 18}px`,
                }}
              >
                {hero.myProfileLabel || 'My Profile'}
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <h2 
          className="text-3xl font-bold text-center mb-12"
          style={{
            color: featuresSection.titleColor || '#1f2937',
            fontSize: `${featuresSection.titleFontSize || 36}px`,
            fontFamily: featuresSection.titleFontFamily || 'system-ui'
          }}
        >
          {featuresSection.title || 'Why Choose Us?'}
        </h2>
        <div className="flex flex-wrap justify-center gap-8 max-w-7xl mx-auto">
          {features.map((feature, index) => {
            return (
              <div key={index} className="card text-center w-full md:w-80 flex-shrink-0">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  {feature.iconType === 'image' && feature.imageUrl ? (
                    <img 
                      src={feature.imageUrl} 
                      alt={feature.title}
                      className="w-12 h-12 object-cover rounded-full"
                    />
                  ) : (
                    <span className="text-4xl">{feature.iconValue || '⭐'}</span>
                  )}
                </div>
                <h3 
                  className="text-xl font-bold mb-3"
                  style={{
                    color: feature.titleColor || '#1f2937',
                    fontSize: `${feature.titleFontSize || 20}px`,
                    fontFamily: feature.titleFontFamily || 'system-ui'
                  }}
                >
                  {feature.title}
                </h3>
                <p 
                  className="text-gray-600"
                  style={{
                    color: feature.descriptionColor || '#4b5563',
                    fontSize: `${feature.descriptionFontSize || 16}px`,
                    fontFamily: feature.descriptionFontFamily || 'system-ui'
                  }}
                >
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Services Overview */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 
            className="text-3xl font-bold text-center mb-12"
            style={{
              color: servicesSection.titleColor || '#1f2937',
              fontSize: `${servicesSection.titleFontSize || 36}px`,
              fontFamily: servicesSection.titleFontFamily || 'system-ui'
            }}
          >
            {servicesSection.title || 'Our Services'}
          </h2>
          
          {/* Services with optional images */}
          <div className="flex gap-8 items-start mb-12">
            {/* Left Image */}
            {images.aboutImageLeft && (
              <div className="hidden lg:block w-64 flex-shrink-0">
                <img 
                  src={images.aboutImageLeft} 
                  alt="About Us" 
                  className="w-full h-auto rounded-lg shadow-lg object-cover sticky top-4"
                  style={{ maxHeight: '350px' }}
                />
              </div>
            )}

            {/* Services Grid */}
            <div className="flex-1">
              <div className="flex flex-wrap justify-center gap-8">
                {services.map((service, index) => (
                  <div key={index} className="border-2 border-gray-200 rounded-xl p-6 w-full md:w-[calc(33.333%-22px)]">
                    <h3 
                      className="text-2xl font-bold mb-3"
                      style={{
                        color: service.titleColor || '#1f2937',
                        fontSize: `${service.titleFontSize || 24}px`,
                        fontFamily: service.titleFontFamily || 'system-ui'
                      }}
                    >
                      {service.title}
                    </h3>
                    <p 
                      className="text-gray-600 mb-4"
                      style={{
                        color: service.descriptionColor || '#4b5563',
                        fontSize: `${service.descriptionFontSize || 16}px`,
                        fontFamily: service.descriptionFontFamily || 'system-ui'
                      }}
                    >
                      {service.description}
                    </p>
                    <p 
                      className="text-primary-600 font-bold"
                      style={{
                        color: service.priceColor || '#2563eb',
                        fontSize: `${service.priceFontSize || 18}px`,
                        fontFamily: service.priceFontFamily || 'system-ui'
                      }}
                    >
                      {service.price}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Image */}
            {images.aboutImageRight && (
              <div className="hidden lg:block w-64 flex-shrink-0">
                <img 
                  src={images.aboutImageRight} 
                  alt="About Us" 
                  className="w-full h-auto rounded-lg shadow-lg object-cover sticky top-4"
                  style={{ maxHeight: '350px' }}
                />
              </div>
            )}
          </div>

          {/* Pricing Table - only show if sizing is enabled and we have services with pricing */}
          {sizingEnabled && bookableServices.length > 0 && sizeCategories.length > 0 && (
            <div className="max-w-5xl mx-auto">
              <h3 className="text-2xl font-bold text-center mb-8">Pricing by Size</h3>
              <div className="bg-gray-50 rounded-xl p-6 border-2 border-gray-200 overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-300">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Service</th>
                      {sizeCategories.map((cat) => (
                        <th key={cat.id} className="text-center py-3 px-4 font-semibold text-gray-700">
                          {cat.category_label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {bookableServices.map((service) => (
                      <tr key={service.id} className="border-b border-gray-200">
                        <td className="py-4 px-4">
                          <div className="font-semibold text-gray-800">{service.name}</div>
                          {service.description && (
                            <div className="text-sm text-gray-500">{service.description}</div>
                          )}
                        </td>
                        {sizeCategories.map((cat) => {
                          const priceInfo = service.pricing?.find(p => p.size === cat.category_name);
                          return (
                            <td key={cat.id} className="text-center py-4 px-4">
                              {priceInfo ? (
                                <>
                                  <div className="text-lg font-bold text-primary-600">
                                    ${priceInfo.price}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {priceInfo.durationMinutes} min
                                  </div>
                                </>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-8 items-center">
            {/* Left Image */}
            {images.ctaImageLeft && (
              <div className="hidden lg:block w-64 flex-shrink-0">
                <img 
                  src={images.ctaImageLeft} 
                  alt="Book Now" 
                  className="w-full h-auto rounded-lg shadow-lg object-cover"
                  style={{ maxHeight: '350px' }}
                />
              </div>
            )}

            {/* CTA Content */}
            <div className="flex-1 text-center">
              <h2 className="text-3xl font-bold mb-4">Ready to Book?</h2>
              <p className="text-xl mb-8 text-gray-100">
                {cta.subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/book" className="btn bg-white text-primary-600 hover:bg-gray-100 text-lg px-8 py-3">
                  Book Appointment
                </Link>
                <Link to="/profile" className="btn border-2 border-white text-white hover:bg-white hover:text-primary-600 text-lg px-8 py-3">
                  {cta.button2Label}
                </Link>
              </div>
            </div>

            {/* Right Image */}
            {images.ctaImageRight && (
              <div className="hidden lg:block w-64 flex-shrink-0">
                <img 
                  src={images.ctaImageRight} 
                  alt="Book Now" 
                  className="w-full h-auto rounded-lg shadow-lg object-cover"
                  style={{ maxHeight: '350px' }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Business Hours Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="card max-w-2xl mx-auto">
            <div className="flex items-center space-x-3 mb-4">
              <Clock className="text-primary-600" size={32} />
              <h2 className="text-3xl font-bold">Business Hours</h2>
            </div>
            <div className="text-gray-700 whitespace-pre-line text-lg leading-relaxed">
              {businessHours || 'Loading...'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
